// Scorecard API Service
import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Helper function to get Firebase auth token
const getAuthToken = async () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }
  return await auth.currentUser.getIdToken();
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint, options = {}) => {
  const token = await getAuthToken();
  const config = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
  }

  return await response.json();
};

// ==================== SCORECARD TEMPLATE APIs ====================

export const scorecardTemplateApi = {
  // Get all templates
  getAllTemplates: async (options = {}) => {
    const { sport, isActive = true } = options;
    const params = new URLSearchParams();
    if (sport) params.append('sport', sport);
    if (isActive !== undefined) params.append('isActive', isActive.toString());
    
    const queryString = params.toString();
    return await apiCall(`/api/scorecard-templates${queryString ? `?${queryString}` : ''}`);
  },

  // Get template by ID
  getTemplateById: async (templateId) => {
    return await apiCall(`/api/scorecard-templates/${templateId}`);
  },

  // Get templates for specific sport
  getTemplatesBySport: async (sport) => {
    return await apiCall(`/api/scorecard-templates/sport/${sport}`);
  }
};

// ==================== PROGRAM SCORECARD APIs ====================

export const programScorecardApi = {
  // Get program's scorecard configuration
  getProgramScorecard: async (programId) => {
    return await apiCall(`/api/programs/${programId}/scorecard`);
  },

  // Configure program scorecard
  configureScorecard: async (programId, templateId) => {
    return await apiCall(`/api/programs/${programId}/scorecard`, {
      method: 'POST',
      body: JSON.stringify({ templateId })
    });
  },

  // Customize scorecard
  customizeScorecard: async (programId, templateData) => {
    return await apiCall(`/api/programs/${programId}/scorecard/customize`, {
      method: 'PUT',
      body: JSON.stringify({ templateData })
    });
  },

  // Remove scorecard configuration
  removeScorecard: async (programId) => {
    return await apiCall(`/api/programs/${programId}/scorecard`, {
      method: 'DELETE'
    });
  },

  // Preview scorecard with sample data
  previewScorecard: async (programId) => {
    return await apiCall(`/api/programs/${programId}/scorecard/preview`);
  }
};

// ==================== LIVE EVENT APIs ====================

export const liveEventApi = {
  // Start event
  startEvent: async (eventId, options = {}) => {
    const { enableAttendance = false } = options;
    return await apiCall(`/api/events/${eventId}/start`, {
      method: 'POST',
      body: JSON.stringify({ enableAttendance })
    });
  },

  // Update score
  updateScore: async (eventId, scoreData) => {
    return await apiCall(`/api/events/${eventId}/score`, {
      method: 'PUT',
      body: JSON.stringify(scoreData)
    });
  },

  // Update statistics
  updateStatistics: async (eventId, team, statistics) => {
    return await apiCall(`/api/events/${eventId}/statistics`, {
      method: 'PUT',
      body: JSON.stringify({ team, statistics })
    });
  },

  // Add event log entry
  addEventLog: async (eventId, eventData) => {
    return await apiCall(`/api/events/${eventId}/events`, {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
  },

  // Mark attendance
  markAttendance: async (eventId, attendees) => {
    return await apiCall(`/api/events/${eventId}/attendance`, {
      method: 'POST',
      body: JSON.stringify({ attendees })
    });
  },

  // Update event metrics (actualAttendees, etc.)
  updateEventMetrics: async (eventId, metrics) => {
    return await apiCall(`/api/events/${eventId}/metrics`, {
      method: 'PATCH',
      body: JSON.stringify({ metrics })
    });
  },

  // Finish event
  finishEvent: async (eventId, summary = '') => {
    return await apiCall(`/api/events/${eventId}/finish`, {
      method: 'POST',
      body: JSON.stringify({ summary })
    });
  },

  // Get all live events
  getLiveEvents: async (options = {}) => {
    const { programId, clubId, limit = 50 } = options;
    const params = new URLSearchParams();
    if (programId) params.append('programId', programId);
    if (clubId) params.append('clubId', clubId);
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    return await apiCall(`/api/events/live${queryString ? `?${queryString}` : ''}`);
  },

  // Get single event with scorecard data
  getEvent: async (eventId) => {
    return await apiCall(`/api/events/${eventId}`);
  },

  // ==================== PERIOD MANAGEMENT ====================
  
  // End current period
  endPeriod: async (eventId) => {
    return await apiCall(`/api/events/${eventId}/periods/end`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  // Start next period
  startPeriod: async (eventId, options = {}) => {
    const { periodNumber, periodName } = options;
    return await apiCall(`/api/events/${eventId}/periods/start`, {
      method: 'POST',
      body: JSON.stringify({ periodNumber, periodName })
    });
  },

  // Get period status
  getPeriodStatus: async (eventId) => {
    return await apiCall(`/api/events/${eventId}/periods/current`);
  },

  // ==================== OVERTIME MANAGEMENT ====================
  
  // Add overtime period
  addOvertimePeriod: async (eventId, options = {}) => {
    const { periodName, overtimeNumber, force } = options;
    return await apiCall(`/api/events/${eventId}/periods/overtime`, {
      method: 'POST',
      body: JSON.stringify({ periodName, overtimeNumber, force })
    });
  },

  // Check overtime eligibility
  checkOvertimeEligibility: async (eventId) => {
    return await apiCall(`/api/events/${eventId}/periods/overtime/eligibility`);
  },

  // End overtime early (sudden death)
  endOvertimeEarly: async (eventId, reason, scoringTeam, minute) => {
    return await apiCall(`/api/events/${eventId}/periods/overtime/end-early`, {
      method: 'POST',
      body: JSON.stringify({ reason, scoringTeam, minute, autoTriggered: false })
    });
  },

  // ==================== TIMER MANAGEMENT ====================
  
  // Get timer state
  getTimer: async (eventId) => {
    return await apiCall(`/api/events/${eventId}/timer`);
  },

  // Start/resume timer
  startTimer: async (eventId, startFromSeconds = null) => {
    // Only send startFromSeconds if explicitly provided
    // If null/undefined, backend will preserve existing elapsed time (for resume)
    const body = startFromSeconds !== null 
      ? { startFromSeconds } 
      : {};
    
    return await apiCall(`/api/events/${eventId}/timer/start`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  // Pause timer
  pauseTimer: async (eventId, reason = null) => {
    return await apiCall(`/api/events/${eventId}/timer/pause`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  // Set timer (manual correction)
  setTimer: async (eventId, seconds, reason = 'correction') => {
    return await apiCall(`/api/events/${eventId}/timer/set`, {
      method: 'PUT',
      body: JSON.stringify({ seconds, reason })
    });
  },

  // Reset timer
  resetTimer: async (eventId, reason = 'reset', startRunning = false) => {
    return await apiCall(`/api/events/${eventId}/timer/reset`, {
      method: 'POST',
      body: JSON.stringify({ reason, startRunning })
    });
  }
};

// ==================== UTILITY FUNCTIONS ====================

export const scorecardUtils = {
  // Check if user can manage scorecard
  canManageScorecard: (userClubs, programId) => {
    // This is a client-side check - server always validates
    return Array.isArray(userClubs) && userClubs.some(m => {
      const role = (m.role || '').toLowerCase();
      return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 
              'programdirector', 'programadmin'].includes(role);
    });
  },

  // Check if user can score events
  canScoreEvent: (userClubs, programId) => {
    return Array.isArray(userClubs) && userClubs.some(m => {
      const role = (m.role || '').toLowerCase();
      return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager',
              'programdirector', 'programadmin', 'headcoach'].includes(role);
    });
  },

  // Format score for display
  formatScore: (score) => {
    if (!score) return '0 - 0';
    return `${score.home || 0} - ${score.away || 0}`;
  },

  // Calculate winner
  getWinner: (score) => {
    if (!score) return null;
    if (score.home > score.away) return 'home';
    if (score.away > score.home) return 'away';
    return 'tie';
  },

  // Format duration
  formatDuration: (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  },

  // Get sport icon
  getSportIcon: (sport) => {
    const icons = {
      'Soccer': '⚽',
      'Basketball': '🏀',
      'Baseball': '⚾',
      'Volleyball': '🏐',
      'Football': '🏈',
      'Hockey': '🏒',
      'Tennis': '🎾',
      'Rugby': '🏉',
      'Lacrosse': '🥍',
      'Cricket': '🏏',
      'Other': '🎯'
    };
    return icons[sport] || '🎯';
  },

  // Validate score update
  validateScoreUpdate: (scoreData, schema) => {
    const errors = [];
    
    if (schema?.scoring) {
      const { homeTeam, awayTeam } = schema.scoring;
      
      if (scoreData.home < (homeTeam.min || 0)) {
        errors.push(`Home score cannot be less than ${homeTeam.min || 0}`);
      }
      
      if (scoreData.away < (awayTeam.min || 0)) {
        errors.push(`Away score cannot be less than ${awayTeam.min || 0}`);
      }
      
      if (homeTeam.max !== undefined && scoreData.home > homeTeam.max) {
        errors.push(`Home score cannot exceed ${homeTeam.max}`);
      }
      
      if (awayTeam.max !== undefined && scoreData.away > awayTeam.max) {
        errors.push(`Away score cannot exceed ${awayTeam.max}`);
      }
    }
    
    return errors;
  }
};

const scorecardApi = {
  scorecardTemplateApi,
  programScorecardApi,
  liveEventApi,
  scorecardUtils
};

export default scorecardApi;